import React, { useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchBulkReactions } from '../slices/reactionsSlice.mjs';
import { fetchBulkRatingAggregates } from '../slices/ratingsSlice.mjs';
import { setPageTitle, setPageDescription, setPageMetadata, setCanonicalUrl, resetPageMetadata } from '../slices/pageMetadataSlice.mjs';
import HomePageContent from './body/Home.mjs';
import { setHeaderData } from '../slices/headerSlice.mjs';
import useIsMounted from '../hooks/useIsMounted.mjs';
import getHomeLayout from './home/layouts/index.mjs';
import { useArticleScope, buildScopeFromParams } from '../hooks/useArticleScope.mjs';
import { setPagination } from '../slices/unifiedArticlesSlice.mjs';
import useSafeSearchParams from '../hooks/useSafeSearchParams.mjs';
import Breadcrumbs from '../components/navigation/Breadcrumbs.mjs';



/**
 * Category Home Page - displays StreamlinedSourceArticle content for a category.
 * Uses unified articles slice with scope-based caching.
 *
 * Route: /:categorySlug (e.g., /sports, /technology)
 * Note: The root route (/) is now handled by LandingPage.
 */
const CategoryHomePage = () => {
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const { categorySlug: paramsSlug } = useParams();
    const [searchParams, setSearchParams] = useSafeSearchParams();

    // On SSR, useParams() returns undefined (no router context).
    // Get currentScope from Redux (set by SSR controller) as fallback.
    const ssrScope = useSelector((state) => state.unifiedArticles?.currentScope);

    // Determine the effective categorySlug: use route params on client, SSR scope as fallback.
    // IMPORTANT: Only use ssrScope during SSR (server). On client, ssrScope is a live Redux
    // value that gets updated during navigation, so reading it here would cause stale data
    // (e.g., showing last-visited category when returning to home).
    const isSSR = typeof window === 'undefined';
    const categorySlug = paramsSlug || (isSSR && ssrScope?.type === 'category' ? ssrScope.category : null);

    // Get category metadata from persistent sources (survives navigation)
    // pureCategories comes from categoriesMiddleware and persists in headerSlice
    const pureCategories = useSelector((state) => state.headerSlice?.pureCategories || []);
    const categoryMeta = useSelector((state) => state.pageMetadata?.metadata);

    // Look up category info from pureCategories (DB-sourced, persists across navigation)
    const categoryFromDB = useMemo(
        () => pureCategories.find((c) => c.name === categorySlug),
        [pureCategories, categorySlug]
    );
    const categoryLabel = categoryFromDB?.label || categoryMeta?.categoryLabel || categorySlug?.charAt(0).toUpperCase() + categorySlug?.slice(1) || 'Category';
    const categoryDescription = categoryFromDB?.description || categoryMeta?.categoryDescription || `Latest ${categoryLabel} news and articles.`;
    // Read the current page from Redux (SSR-safe: set by SSR controller in preloadedState)
    // This MUST be read before useArticleScope to avoid circular dependency
    const reduxPage = useSelector(state => state.unifiedArticles?.pagination?.page || 1);

    // Build category scope (includes page for cache key partitioning)
    const scope = useMemo(() => {
        if (!categorySlug) {
            // No slug = root route = featured-categories home page
            return { type: 'home' };
        }
        const baseScope = buildScopeFromParams(categorySlug);
        return reduxPage > 1 ? { ...baseScope, page: reduxPage } : baseScope;
    }, [categorySlug, reduxPage]);

    // Use the unified article scope hook
    const {
        articles: articlesToDisplay,
        isLoading,
        error,
        pagination,
        scopeKey,
        refetch,
    } = useArticleScope(scope);

    // Get layout settings from Redux (set by middleware)
    const layoutSettings = useSelector((state) => state.pageLayoutSettings?.current);
    const articlesPerPage = layoutSettings?.pagination?.articlesPerPage || 25;

    // Get user's homepage layout preference
    const userLayoutPref = useSelector(
        (state) => state.pageLayoutSettings?.userLayoutPreferences?.homepage || 'default'
    );
    const LayoutWrapper = useMemo(() => getHomeLayout(userLayoutPref), [userLayoutPref]);

    // Reactions state
    const {
        byArticleId: reactionsByArticleId,
        loading: reactionsLoading,
        fetched: reactionsFetched,
    } = useSelector(
        (state) =>
            state.reactions || {
                byArticleId: {},
                loading: { bulk: false },
                fetched: {},
            }
    );

    // Current page from Redux (SSR-safe)
    const currentPage = pagination?.page || 1;

    // Extract all article IDs for bulk reaction loading (deduplicated)
    const allArticleIds = useMemo(() => {
        if (!Array.isArray(articlesToDisplay)) return [];

        const ids = articlesToDisplay.flatMap((group) =>
            group && Array.isArray(group.articles)
                ? group.articles.map((article) => article?._id).filter(Boolean)
                : []
        );
        // Deduplicate - same article can appear in multiple categories
        return [...new Set(ids)];
    }, [articlesToDisplay]);

    // Determine which article IDs need reactions fetched
    const unfetchedReactionIds = useMemo(() => {
        return allArticleIds.filter(
            (articleId) => !reactionsFetched[articleId] && !reactionsByArticleId[articleId]
        );
    }, [allArticleIds, reactionsFetched, reactionsByArticleId]);

    // On mount or category change, set page metadata
    useEffect(() => {
        // Use 'Home' title/description on root route, category values otherwise
        const isRootRoute = !paramsSlug || categoryMeta?.isRootRoute === 'true' || categoryMeta?.isRootRoute === true;
        const pageTitle = isRootRoute
            ? 'Home - Nexus of Truth'
            : `${categoryLabel} - Nexus of Truth`;
        const pageDescription = isRootRoute
            ? 'Discover the truth through articles and insights.'
            : categoryDescription;
        dispatch(setPageTitle(pageTitle));
        dispatch(setPageDescription(pageDescription));
        const baseUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';
        const canonicalUrl = isRootRoute ? `${baseUrl}/` : `${baseUrl}/${paramsSlug}`;
        dispatch(setCanonicalUrl(canonicalUrl));
        // Extract first available article image for social cards
        let firstImage = null;
        if (Array.isArray(articlesToDisplay)) {
            for (const group of articlesToDisplay) {
                if (group?.articles?.length) {
                    for (const a of group.articles) {
                        const img = a?.generatedImages?.[0]?.url || a?.images?.[0]?.url;
                        if (img) { firstImage = img; break; }
                    }
                }
                if (firstImage) break;
            }
        }
        dispatch(setPageMetadata({
            categoryCode: categorySlug,
            categoryLabel: categoryLabel,
            categoryDescription: categoryDescription,
            'og:title': pageTitle,
            'og:description': pageDescription,
            'og:type': 'website',
            'og:url': canonicalUrl,
            ...(firstImage ? { 'og:image': firstImage } : {}),
            'twitter:card': firstImage ? 'summary_large_image' : 'summary',
            'twitter:title': pageTitle,
            'twitter:description': pageDescription,
            ...(firstImage ? { 'twitter:image': firstImage } : {}),
        }));
        dispatch(
            setHeaderData({
                title: pageTitle,
                description: pageDescription,
            })
        );

        return () => {
            dispatch(resetPageMetadata());
        };
    }, [dispatch, categoryLabel, categoryDescription, paramsSlug, categoryMeta?.isRootRoute, articlesToDisplay]);

    // Smart bulk reactions fetching
    useEffect(() => {
        const shouldFetchReactions =
            unfetchedReactionIds.length > 0 && !reactionsLoading.bulk && !isLoading;

        if (shouldFetchReactions) {
            console.log(
                `CategoryHomePage [${categorySlug}]: Loading reactions for ${unfetchedReactionIds.length} new articles`
            );
            dispatch(fetchBulkReactions({ articleIds: unfetchedReactionIds }));
        }
    }, [unfetchedReactionIds, dispatch, reactionsLoading.bulk, isLoading, categorySlug]);

    // Rating aggregates — separate effect so reaction state changes don't re-trigger
    useEffect(() => {
        if (allArticleIds.length > 0 && !isLoading) {
            dispatch(fetchBulkRatingAggregates({ articleIds: allArticleIds }));
        }
    }, [allArticleIds, dispatch, isLoading]);

    // UI state calculations
    const showLoading = isLoading && articlesToDisplay.length === 0;
    const noArticlesAvailable = !showLoading && articlesToDisplay.length === 0;
    const hasError = !!error;

    // Sync URL page to Redux on mount and when URL changes (back/forward)
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    useEffect(() => {
        if (isMounted && urlPage !== currentPage) {
            dispatch(setPagination({ page: urlPage }));
        }
    }, [urlPage, isMounted]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle page change - update URL (enables browser history)
    const handlePageChange = useCallback((newPage, updateUrl = true) => {
        if (updateUrl) {
            setSearchParams({ page: newPage.toString() });
        }

        // Update pagination — scope change via URL triggers auto-fetch in useArticleScope
        dispatch(setPagination({ page: newPage }));

        // Scroll to top on page change
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [setSearchParams, dispatch]);

    return (
        <>
            <main className={`home-page-container category-home-page category-${categorySlug}`}>
                <Breadcrumbs />
                <LayoutWrapper>
                    {showLoading ? (
                        <div className="home-loading-container">
                            <div className="home-loading-spinner"></div>
                            <p>Loading the latest {categoryLabel.toLowerCase()} news...</p>
                        </div>
                    ) : hasError ? (
                        <div className="home-error-message">
                            <h2>Error loading articles</h2>
                            <p>{error?.toString() || 'An unknown error occurred'}</p>
                            <button onClick={() => refetch()}>
                                Try Again
                            </button>
                        </div>
                    ) : noArticlesAvailable ? (
                        <div className="home-no-articles-message">
                            <h2>No {categoryLabel.toLowerCase()} articles available</h2>
                            <p>Check back soon for the latest {categoryLabel.toLowerCase()} content.</p>
                        </div>
                    ) : (
                        <HomePageContent
                            articlesGroupedByCategory={articlesToDisplay}
                            linkContext={categorySlug ? { categorySlug } : undefined}
                            pagination={pagination}
                            onPageChange={handlePageChange}
                        />
                    )}
                </LayoutWrapper>
            </main>
        </>
    );
};

export default CategoryHomePage;
